/**
 * What the Scheduled Scans page says before you read a row.
 *
 * The table is good at "what did I set up" and cannot answer "what changed, is
 * anything wrong" without reading every row. This band answers that, and it
 * counts FINDINGS rather than schedules on purpose: see scheduleStats.ts for
 * the measurement that drove the choice.
 *
 * Reuses the shared StatBand so it cannot drift from the dashboard home's
 * stat row. It deliberately renders NO trend chip -- QuickStatsSection's chip
 * is green-when-up because more issues filed means more QA done, and these
 * findings are problems on the user's own site, where up is the bad direction.
 */
import { StatBand, type StatBandCell } from '../shared/StatBand';
import { summarizeSchedules } from './scheduleStats';
import type { ScheduledScanRow } from '../../lib/api/scheduledScans';

interface ScheduleStatsBandProps {
  schedules: readonly ScheduledScanRow[];
  /** Passed in so "overdue" is deterministic per render pass, as the table does. */
  now: Date;
}

/**
 * The value for a delta that does not exist yet.
 *
 * NOT "0", which claims a scan compared two runs and found no change, and NOT
 * a bare dash: scheduleRowCopy.nextRunLabel already rejected that as ambiguous
 * (unknown? never? zero?) and chose words for exactly this reason.
 */
const NO_COMPARISON_LABEL = 'None yet';

/** The value for a page that has not been scanned at all. */
const NOT_SCANNED_LABEL = 'Not scanned yet';

/** A state, not a quantity: smaller than the numerals it sits beside. */
function stateValue(label: string) {
  return <span className="text-base font-medium text-gray-500">{label}</span>;
}

export function ScheduleStatsBand({ schedules, now }: ScheduleStatsBandProps) {
  // Not memoized, deliberately, and for the same reason SchedulePage rebuilds
  // its column array every render: TanStack hands back the same `schedules`
  // reference from cache, so a memo keyed on it would keep serving numbers
  // computed against the clock at first paint. One loop over a few dozen rows
  // is cheaper than the staleness that caused ("in 6 hrs" hours after it was
  // 2, scheduleColumns' own note).
  const stats = summarizeSchedules(schedules, now);

  // No schedules means the EmptyState below is doing the explaining. Four
  // zeroes above an empty table teach nothing and push the empty state down.
  if (schedules.length === 0) return null;

  const cells: StatBandCell[] = [
    {
      key: 'findings',
      label: 'Open findings',
      value: stats.findings === null ? stateValue(NOT_SCANNED_LABEL) : stats.findings,
      title:
        stats.findings === null
          ? 'No page has been scanned yet, so there is nothing to report. This is not a clean result.'
          : 'Findings from the most recent scan of every page you watch, paused pages included.',
    },
    {
      key: 'new',
      label: 'New since last scan',
      value:
        stats.newFindings === null ? stateValue(NO_COMPARISON_LABEL) : stats.newFindings,
      // The one reward this feature can give, paired with the bad news rather
      // than given a cell of its own: new and fixed are two halves of the same
      // delta, and the 4-cell grid has no fifth slot. Green is unambiguous HERE
      // in a way it is not on the value beside it -- fixing things is good in
      // every reading, so this chip carries no direction trap.
      chip:
        stats.fixed !== null && stats.fixed > 0 ? (
          <span className="inline-flex items-center px-2 py-[3px] rounded-full text-xs font-semibold tabular-nums bg-[var(--color-trend-up-bg)] text-[var(--color-trend-up-text)]">
            {stats.fixed} fixed
          </span>
        ) : undefined,
      title:
        stats.newFindings === null
          ? 'Every page is still on its first scan, so there is nothing to compare against yet.'
          : 'Findings that appeared since the previous scan of the same page.',
    },
    {
      key: 'watched',
      label: 'Pages watched',
      value: stats.watched,
      title: 'Pages with an active schedule. Paused pages are not counted.',
    },
    {
      key: 'attention',
      label: 'Needs attention',
      // Colour REINFORCES the label and never carries it: the eyebrow reads
      // "Needs attention" at zero too (WCAG 1.4.1). yellow-800 is the same step
      // the Next-scan cell uses for an overdue run, so the summary and the row
      // it summarises cannot disagree, and it is the one that clears 4.5:1 on
      // both white and DataTable's odd-row gray-50/60 stripe.
      value:
        stats.attention > 0 ? (
          <span className="text-yellow-800">{stats.attention}</span>
        ) : (
          stats.attention
        ),
      title:
        'Pages whose review could not be completed, or whose scan is overdue. Pages you paused are not counted.',
    },
  ];

  return <StatBand id="schedule-stats" ariaLabel="Scheduled scan summary" cells={cells} />;
}
